// src/render/map/tilemap-loader.ts — convert Phaser Tilemap to plain MapData.
//
// Invariants:
//   - Called once during GameScene.create() after the tilemap + layers are built.
//   - Returns a plain-data `MapData` (NO Phaser objects) so sim can consume it
//     via `setMapData()` without tripping ESLint sim-boundary rules.
//   - Handles BOTH rectangle-shaped zone objects and polygon-shaped zone objects
//     (Open Question 2 in RESEARCH.md): rectangles are normalised to 4-vertex
//     polygons with absolute world-pixel coords.
//   - NULL-safe on missing zones/spawn object layers (placeholder may omit them).
//
// Phaser 4 ESM has NO default export (Pitfall 2): always namespace import.
// Source: Phase 1 RESEARCH.md §Pattern 2, PATTERNS.md §tilemap-loader.ts.

import type * as Phaser from 'phaser';
import type {
  MapData,
  WalkabilityGrid,
  ZonePolygon,
  ZoneId,
  Vec2,
  LightAuth,
  HidingSpotAuth,
  HidingSpotSize,
  HidingSpotKind,
  FearTriggerAuth,
  DogAuth,
  AdultAuth,
  SurfacePolygon,
  RoofPolygon,
  TreeObject,
  WallPolygon,
  ObstaclePolygon,
  FencePolyline,
} from '../../shared/types/map';

/**
 * Phase 08.1: map from surface-types.json `type` key → packed 24-bit hex colour.
 * Inlined so the loader has no runtime JSON read and stays synchronous.
 * Default (unlisted type) falls back to 'scrub' colour.
 */
const SURFACE_COLORS: Record<string, number> = {
  'scrub':             0xc4a878,
  'packed-earth-road': 0xb08d5c,
  'rough-ground':      0x9a8a5a,
  'very-rough-ground': 0x7d7048,
  'stone-courtyard':   0xa9a294,
  'pool-terrace':      0xd8cfb8,
  'pool-water':        0x3a8fbf,
} as const;

/** Default surface colour when the surface type is absent or unrecognised. */
const SURFACE_COLOR_DEFAULT = SURFACE_COLORS['scrub']!;

/**
 * Phase 08.1 Item 2: map from surface-types.json `type` key → walking-speed multiplier.
 * Values from surface-types.json. Default 1.0 (scrub) for unlisted types.
 */
const SURFACE_SPEEDS: Record<string, number> = {
  'scrub':             1.0,
  'packed-earth-road': 1.1,
  'rough-ground':      0.75,
  'very-rough-ground': 0.5,
  'stone-courtyard':   1.05,
  'pool-terrace':      1.0,
  'pool-water':        0.0,
} as const;

/** Default surface speed when the surface type is absent or unrecognised. */
const SURFACE_SPEED_DEFAULT = SURFACE_SPEEDS['scrub']!;

export function extractMapData(
  map: Phaser.Tilemaps.Tilemap,
  collisionLayer: Phaser.Tilemaps.TilemapLayer,
): MapData {
  // 1. Walkability grid: collisionLayer.getTileAt(x, y).collides drives 1/0.
  //    Bgrins pathfinding convention: 0 = walkable, 1 = blocked.
  //
  //    `collidableTileCount` is a sanity counter surfaced in MapData so tests
  //    (and production diagnostics) can assert the collision layer is non-empty
  //    after load. Plan 01-07 UAT Test 1 Gap B: if this count is zero on the
  //    production map, the downstream Arcade collider is a no-op and the
  //    character walks through walls.
  let collidableTileCount = 0;
  const grid: WalkabilityGrid = [];
  for (let y = 0; y < map.height; y++) {
    const row: number[] = [];
    for (let x = 0; x < map.width; x++) {
      const tile = collisionLayer.getTileAt(x, y);
      const collides = Boolean(tile && tile.collides);
      if (collides) collidableTileCount++;
      row.push(collides ? 1 : 0);
    }
    grid.push(row);
  }

  // 2. Zones: extract from the `zones` object layer; accept polygon or rectangle.
  const zones = new Map<ZoneId, ZonePolygon>();
  const zoneLayer = map.getObjectLayer('zones');
  if (zoneLayer) {
    for (const obj of zoneLayer.objects) {
      if (!obj.name) continue;
      const vertices = verticesFromTiledObject(obj);
      if (vertices.length < 3) continue;
      zones.set(obj.name, { name: obj.name, vertices });
    }
  }

  // 3. Player spawn: find `spawn.player` point object in the `spawn` layer.
  let playerSpawn: Vec2 | null = null;
  const spawnObj = map.findObject('spawn', (o) => o.name === 'spawn.player');
  if (spawnObj && typeof spawnObj.x === 'number' && typeof spawnObj.y === 'number') {
    playerSpawn = { x: spawnObj.x, y: spawnObj.y };
  }

  // 4. Lights: extract from the `lights` object layer (P2 D-01/D-02). Point objects only.
  //    Null-safe: missing layer → empty array (not an error). Per Security Domain V5
  //    + Pitfall #5, malformed per-object fields (non-finite x/y) are skipped so an
  //    author error in the .tmj never crashes the scene.
  const lights: LightAuth[] = extractLights(map);

  // 5. Hiding spots: P3 D-19 — parse authored `hiding-spots` object layer.
  // Null-safe: missing layer → empty array. Malformed (non-finite coords) or
  // kind='on-floor' (synthetic-only) objects are skipped.
  //
  // P3 Plan 03-07 (UAT Test 6 follow-up) — walkability snap: Tiled authors
  // typically place the spot centre ON the landmark (trunk/pot/bench), which
  // frequently lands inside a wall tile. The sim's proximity check uses
  // `spot.position` directly, so a wall-centred spot is unreachable — the
  // player cannot get within 32 px from a walkable tile. BFS each
  // wall-centred spot to the nearest walkable tile and snap the position to
  // that tile's centre. Keeps mouchao.tmj pristine (content is authored
  // against the narrative anchor); only the runtime gameplay position
  // moves to where the player can actually stand.
  const hidingSpots: HidingSpotAuth[] = snapHidingSpotsToWalkable(
    extractHidingSpots(map),
    grid,
    map.width,
    map.height,
    map.tileWidth,
    map.tileHeight,
  );

  // 6. Fear triggers: P4 D-22 — parse `fear-triggers` object layer.
  const fearTriggers: FearTriggerAuth[] = extractFearTriggers(map);

  // 7. Dog spawns: P4 D-23 — parse `dogs` object layer.
  const dogs: DogAuth[] = extractDogs(map);

  // 8. Adult spawns: P4 D-26 — parse `adults` object layer.
  const adults: AdultAuth[] = extractAdults(map);

  // 9. Phase 08.1: polygon layers for crisp rendering.
  //    Null-safe: missing layer → empty array (renderer falls back to camera bg).
  const surfaces: SurfacePolygon[] = extractSurfaces(map);
  const roofs: RoofPolygon[] = extractRoofs(map);
  const trees: TreeObject[] = extractTrees(map);

  // 10. Phase 08.1 Item 2: per-tile speed grid rasterized from surfaces polygons.
  //     Optional: absent from sim-test minimal MapData (no surfaces → all-1.0 default).
  const speedGrid: Float32Array = buildSpeedGrid(
    surfaces,
    map.width,
    map.height,
    map.tileWidth,
    map.tileHeight,
  );

  // 11. Phase 08.1 Item 1: wall polygons for shape-precise movement collision.
  //     Extracted from the `walls` object layer (real Mouchao angles, ~24°).
  //     When present, movement.ts uses circle-vs-polygon instead of tile-grid.
  //     Absent → falls back to tile-grid (movementGrid) for sim-test maps.
  const walls: WallPolygon[] = extractWalls(map);

  // 11b. Phase 08.1 Item 2: obstacles and fences — block movement, NOT sight.
  //     Extracted from Tiled object layers; absent layers → empty arrays (null-safe).
  const obstacles: ObstaclePolygon[] = extractObstacles(map);
  const fences: FencePolyline[] = extractFences(map);

  // 11c. Phase 08.1 Item 3: out-of-bounds boundary — the playable-area polygon.
  //      Keeps the character inside the authored farm extent (shape-precise);
  //      the converter also rasterizes outside-the-boundary as collision tiles
  //      for A* and FOV. Absent layer → empty array (map is unbounded).
  const boundary: Vec2[] = extractBoundary(map);

  // 12. Phase 08.1 Item 2: movement grid — copy of sight grid + obstacles/fences
  //     rasterized. Used by pathfinding + wall-clamping. FOV keeps using `grid`.
  const movementGrid: WalkabilityGrid = buildMovementGrid(
    grid,
    obstacles,
    fences,
    map.width,
    map.height,
    map.tileWidth,
    map.tileHeight,
  );

  return {
    grid,
    zones,
    tileWidth: map.tileWidth,
    tileHeight: map.tileHeight,
    widthInTiles: map.width,
    heightInTiles: map.height,
    collidableTileCount,
    playerSpawn,
    lights,
    hidingSpots,
    fearTriggers,
    dogs,
    adults,
    surfaces,
    roofs,
    trees,
    walls,
    speedGrid,
    obstacles,
    fences,
    movementGrid,
    boundary,
  };
}

/**
 * Extract authored lights from the Tiled `lights` object layer into LightAuth[].
 *
 * Defaults (D-02): type='permanent', radius=5 tiles, color=0xe8a838 (warm amber),
 * intensity=1, triggerRadius = radius * 1.2 (motion only; 0 for permanent).
 *
 * Null-safe: missing layer → []. Skips malformed objects (non-finite coords).
 */
function extractLights(map: Phaser.Tilemaps.Tilemap): LightAuth[] {
  const lights: LightAuth[] = [];
  const lightsLayer = map.getObjectLayer('lights');
  if (!lightsLayer) return lights;

  for (const obj of lightsLayer.objects) {
    if (typeof obj.x !== 'number' || !Number.isFinite(obj.x)) continue;
    if (typeof obj.y !== 'number' || !Number.isFinite(obj.y)) continue;

    // Properties come as Tiled's [{ name, value, type? }] array. Build a Map for lookup.
    const props = new Map<string, unknown>();
    for (const p of obj.properties ?? []) {
      props.set(p.name, p.value);
    }

    const typeRaw = props.get('type');
    const lightType: 'permanent' | 'motion' =
      typeRaw === 'motion' ? 'motion' : 'permanent'; // default permanent per D-02

    const radiusRaw = props.get('radius');
    const radius =
      typeof radiusRaw === 'number' && Number.isFinite(radiusRaw) ? radiusRaw : 5;

    const intensityRaw = props.get('intensity');
    const intensity =
      typeof intensityRaw === 'number' && Number.isFinite(intensityRaw)
        ? Math.max(0, Math.min(1, intensityRaw))
        : 1;

    const colorRaw = props.get('color');
    let color = 0xe8a838; // D-02 default warm amber
    if (typeof colorRaw === 'string' && colorRaw.startsWith('#')) {
      const parsed = Number.parseInt(colorRaw.slice(1), 16);
      if (Number.isFinite(parsed)) color = parsed;
    } else if (typeof colorRaw === 'number' && Number.isFinite(colorRaw)) {
      color = colorRaw;
    }

    const triggerRadiusRaw = props.get('triggerRadius');
    let triggerRadius = 0;
    if (lightType === 'motion') {
      triggerRadius =
        typeof triggerRadiusRaw === 'number' && Number.isFinite(triggerRadiusRaw)
          ? triggerRadiusRaw
          : radius * 1.2; // D-02 + Pitfall #18 default
    }

    // Phase 08.1 Item 3: kind / angleDeg / spreadDeg (static-light rendering mode).
    // 'cone' → directional torch-cone sprite; anything else → 'omni' (default radial glow).
    const kindRaw = props.get('kind');
    const kind: 'omni' | 'cone' = kindRaw === 'cone' ? 'cone' : 'omni';

    const angleDegRaw = props.get('angleDeg');
    const angleDeg: number =
      typeof angleDegRaw === 'number' && Number.isFinite(angleDegRaw) ? angleDegRaw : 0;

    const spreadDegRaw = props.get('spreadDeg');
    const spreadDeg: number =
      typeof spreadDegRaw === 'number' && Number.isFinite(spreadDegRaw) ? spreadDegRaw : 45;

    const id = obj.name && obj.name.length > 0 ? obj.name : `light${lights.length}`;

    lights.push({
      id,
      position: { x: obj.x, y: obj.y },
      radius,
      type: lightType,
      color,
      intensity,
      triggerRadius,
      kind,
      angleDeg,
      spreadDeg,
    });
  }

  return lights;
}

/**
 * P3 D-19: extract authored hiding spots from the Tiled `hiding-spots` object
 * layer into HidingSpotAuth[].
 *
 * Defaults when a property is missing:
 *   size = 'small'
 *   kind = 'other'
 *   skillMod = 0.5 (clamped to [0, 1] when authored)
 *   requiredAbilities = [] (parsed from CSV string)
 *   forbiddenCharacters = [] (parsed from CSV string)
 *   peekEnabled = true
 *
 * Null-safe: missing layer → []. Skips malformed objects (non-finite coords)
 * and rejects `kind='on-floor'` per D-19 (synthetic-only; the sim conjures
 * floor spots via createFloorSpot, never Tiled authoring).
 */
export function extractHidingSpots(map: Phaser.Tilemaps.Tilemap): HidingSpotAuth[] {
  const spots: HidingSpotAuth[] = [];
  const layer = map.getObjectLayer('hiding-spots');
  if (!layer) return spots;

  const VALID_SIZES: HidingSpotSize[] = ['small', 'large'];
  // D-19: 'on-floor' is synthetic-only; the sim conjures it via createFloorSpot.
  // Authoring an 'on-floor' Tiled spot is rejected (skipped) so a hand-authored
  // mistake cannot leak into the extracted array.
  const VALID_AUTHORED_KINDS: HidingSpotKind[] = [
    'tree', 'pot', 'car', 'under-object', 'behind-door', 'other',
  ];

  for (const obj of layer.objects) {
    if (typeof obj.x !== 'number' || !Number.isFinite(obj.x)) continue;
    if (typeof obj.y !== 'number' || !Number.isFinite(obj.y)) continue;

    const props = new Map<string, unknown>();
    for (const p of obj.properties ?? []) {
      props.set(p.name, p.value);
    }

    // size gate
    const sizeRaw = props.get('size');
    const size: HidingSpotSize =
      typeof sizeRaw === 'string' && VALID_SIZES.includes(sizeRaw as HidingSpotSize)
        ? (sizeRaw as HidingSpotSize)
        : 'small';

    // kind gate — reject 'on-floor' explicitly
    const kindRaw = props.get('kind');
    if (kindRaw === 'on-floor') continue;
    const kind: HidingSpotKind =
      typeof kindRaw === 'string' && VALID_AUTHORED_KINDS.includes(kindRaw as HidingSpotKind)
        ? (kindRaw as HidingSpotKind)
        : 'other';

    const skillModRaw = props.get('skillMod');
    const skillMod =
      typeof skillModRaw === 'number' && Number.isFinite(skillModRaw)
        ? Math.max(0, Math.min(1, skillModRaw))
        : 0.5;

    const requiredAbilitiesRaw = props.get('requiredAbilities');
    const requiredAbilities =
      typeof requiredAbilitiesRaw === 'string'
        ? requiredAbilitiesRaw.split(',').map((s) => s.trim()).filter(Boolean)
        : [];

    const forbiddenCharactersRaw = props.get('forbiddenCharacters');
    const forbiddenCharacters =
      typeof forbiddenCharactersRaw === 'string'
        ? forbiddenCharactersRaw.split(',').map((s) => s.trim()).filter(Boolean)
        : [];

    const peekEnabledRaw = props.get('peekEnabled');
    const peekEnabled =
      typeof peekEnabledRaw === 'boolean' ? peekEnabledRaw : true;

    const id = obj.name && obj.name.length > 0 ? obj.name : `spot${spots.length}`;

    spots.push({
      id,
      position: { x: obj.x, y: obj.y },
      size,
      kind,
      skillMod,
      requiredAbilities,
      forbiddenCharacters,
      peekEnabled,
    });
  }

  return spots;
}

/**
 * P3 Plan 03-07 (UAT Test 6 follow-up) — walkability snap.
 *
 * Tiled authors place a hiding spot ON the landmark tile (the tree trunk,
 * the pot sprite, the bench) — which is frequently the centre of a
 * collidable tile. The sim's proximity check uses spot.position directly,
 * so a wall-centred spot is practically unreachable from any walkable
 * tile (player.position is clamped 16 px from any wall, spot centre is
 * typically ≥32 px deeper into the wall block). The Hide button never
 * arms.
 *
 * For each spot whose centre lands on a wall tile, BFS outward and snap
 * `position` to the centre of the nearest walkable tile. Spots already
 * on walkable tiles are passed through unchanged.
 *
 * BFS radius capped at 10 tiles to keep the worst case bounded (if a
 * spot is deep inside an enclosed room, it stays at its authored
 * position — the gameplay effect is the same as before the snap).
 *
 * Exported for unit-test coverage; the production path goes through
 * extractMapData (it calls this after extractHidingSpots).
 */
export function snapHidingSpotsToWalkable(
  spots: HidingSpotAuth[],
  grid: WalkabilityGrid,
  widthInTiles: number,
  heightInTiles: number,
  tileWidth: number,
  tileHeight: number,
): HidingSpotAuth[] {
  const isWall = (tx: number, ty: number): boolean => {
    if (tx < 0 || tx >= widthInTiles || ty < 0 || ty >= heightInTiles) return true;
    return grid[ty]![tx] === 1;
  };
  const out: HidingSpotAuth[] = [];
  for (const spot of spots) {
    const tx0 = Math.floor(spot.position.x / tileWidth);
    const ty0 = Math.floor(spot.position.y / tileHeight);
    // Only snap if the original tile is in-bounds AND on a wall. Out-of-bounds
    // authored positions are passed through unchanged so the 2×2 fixture tests
    // in tilemap-loader.test.ts don't accidentally trigger the snap. (A real
    // mouchao-scale map would have caught any out-of-bounds author error via
    // a different validation layer.)
    const inBounds =
      tx0 >= 0 && tx0 < widthInTiles && ty0 >= 0 && ty0 < heightInTiles;
    if (!inBounds || !isWall(tx0, ty0)) {
      out.push(spot);
      continue;
    }
    // BFS to nearest walkable tile. 4-connected neighbours are enough — the
    // outer ring of an 8-connected block always has at least one 4-neighbour
    // of any internal tile, so 4-BFS always finds the same closest tile.
    const queue: Array<[number, number, number]> = [[tx0, ty0, 0]];
    const seen = new Set<string>();
    let snapped: { tx: number; ty: number } | null = null;
    while (queue.length > 0) {
      const [x, y, d] = queue.shift()!;
      const key = `${x},${y}`;
      if (seen.has(key)) continue;
      seen.add(key);
      if (!isWall(x, y)) {
        snapped = { tx: x, ty: y };
        break;
      }
      if (d >= 10) continue;
      queue.push([x + 1, y, d + 1]);
      queue.push([x - 1, y, d + 1]);
      queue.push([x, y + 1, d + 1]);
      queue.push([x, y - 1, d + 1]);
    }
    if (snapped) {
      out.push({
        ...spot,
        position: {
          x: snapped.tx * tileWidth + tileWidth / 2,
          y: snapped.ty * tileHeight + tileHeight / 2,
        },
      });
    } else {
      out.push(spot);
    }
  }
  return out;
}

/**
 * P4 D-22: extract fear-trigger point objects from the Tiled `fear-triggers` layer.
 *
 * Defaults: kind='scorpion', radiusTiles=3.
 * Null-safe: missing layer → []. Skips malformed (non-finite x/y).
 */
function extractFearTriggers(map: Phaser.Tilemaps.Tilemap): FearTriggerAuth[] {
  const out: FearTriggerAuth[] = [];
  const layer = map.getObjectLayer('fear-triggers');
  if (!layer) return out;
  for (const obj of layer.objects) {
    if (typeof obj.x !== 'number' || !Number.isFinite(obj.x)) continue;
    if (typeof obj.y !== 'number' || !Number.isFinite(obj.y)) continue;
    const props = new Map<string, unknown>();
    for (const p of obj.properties ?? []) props.set(p.name, p.value);
    const kindRaw = props.get('kind');
    const kind: 'scorpion' | 'snake' = kindRaw === 'snake' ? 'snake' : 'scorpion';
    const radiusTilesRaw = props.get('radiusTiles');
    const radiusTiles =
      typeof radiusTilesRaw === 'number' && Number.isFinite(radiusTilesRaw)
        ? radiusTilesRaw
        : 3;
    const id = obj.name && obj.name.length > 0 ? obj.name : `fearTrigger${out.length}`;
    out.push({ id, position: { x: obj.x, y: obj.y }, kind, radiusTiles });
  }
  return out;
}

/**
 * P4 D-23: extract dog spawn point objects from the Tiled `dogs` layer.
 *
 * Defaults: id fallback `dog${idx}`, wanderZonePolygonId absent when not authored.
 * Null-safe: missing layer → []. Skips malformed (non-finite x/y).
 */
function extractDogs(map: Phaser.Tilemaps.Tilemap): DogAuth[] {
  const out: DogAuth[] = [];
  const layer = map.getObjectLayer('dogs');
  if (!layer) return out;
  for (const obj of layer.objects) {
    if (typeof obj.x !== 'number' || !Number.isFinite(obj.x)) continue;
    if (typeof obj.y !== 'number' || !Number.isFinite(obj.y)) continue;
    const props = new Map<string, unknown>();
    for (const p of obj.properties ?? []) props.set(p.name, p.value);
    const zoneRaw = props.get('wanderZonePolygonId');
    const wanderZonePolygonId =
      typeof zoneRaw === 'string' && zoneRaw.length > 0 ? zoneRaw : undefined;
    const id = obj.name && obj.name.length > 0 ? obj.name : `dog${out.length}`;
    const entry: DogAuth = { id, position: { x: obj.x, y: obj.y } };
    if (wanderZonePolygonId !== undefined) entry.wanderZonePolygonId = wanderZonePolygonId;
    out.push(entry);
  }
  return out;
}

/**
 * P4 D-26: extract adult spawn point objects from the Tiled `adults` layer.
 *
 * Defaults: facingDeg=0, commentaryTag absent when not authored.
 * Null-safe: missing layer → []. Skips malformed (non-finite x/y).
 */
function extractAdults(map: Phaser.Tilemaps.Tilemap): AdultAuth[] {
  const out: AdultAuth[] = [];
  const layer = map.getObjectLayer('adults');
  if (!layer) return out;
  for (const obj of layer.objects) {
    if (typeof obj.x !== 'number' || !Number.isFinite(obj.x)) continue;
    if (typeof obj.y !== 'number' || !Number.isFinite(obj.y)) continue;
    const props = new Map<string, unknown>();
    for (const p of obj.properties ?? []) props.set(p.name, p.value);
    const facingDegRaw = props.get('facingDeg');
    const facingDeg =
      typeof facingDegRaw === 'number' && Number.isFinite(facingDegRaw)
        ? facingDegRaw
        : 0;
    const tagRaw = props.get('commentaryTag');
    const commentaryTag =
      typeof tagRaw === 'string' && tagRaw.length > 0 ? tagRaw : undefined;
    const id = obj.name && obj.name.length > 0 ? obj.name : `adult${out.length}`;
    const entry: AdultAuth = { id, position: { x: obj.x, y: obj.y }, facingDeg };
    if (commentaryTag !== undefined) entry.commentaryTag = commentaryTag;
    out.push(entry);
  }
  return out;
}

/**
 * Count collidable tiles in a parsed .tmj JSON object.
 *
 * PARITY CONTRACT (Plan 01-08 Warning #6): this is the SINGLE SOURCE OF TRUTH
 * for "how many collidable tiles does this map have?". Both the production
 * Phaser runtime loader (via extractMapData, which uses TilemapLayer.getTileAt
 * to reach the same tiles post-Phaser-parse) and the CI audit script
 * (scripts/audit-mouchao-map.ts, which walks the JSON directly) MUST use
 * this helper. A divergence means audit passes while runtime has zero
 * collidable tiles (or vice versa) — the exact class of bug Gap B shipped.
 *
 * The integration test src/render/map/__tests__/tilemap-loader-audit-parity.test.ts
 * further locks this: it imports this helper, invokes audit-mouchao-map.ts as
 * a subprocess, and asserts both return the same count for the same .tmj.
 *
 * Algorithm: find every tileset tile with a `collides: true` custom property,
 * compute its global gid via firstgid + id, then scan every layer of type
 * 'tilelayer' (not just a layer named 'collision' — some .tmj files may
 * distribute collidable tiles across layers; the Phaser runtime counts them
 * all because setCollisionByProperty applies tilewise).
 *
 * @param tmj parsed .tmj JSON (from JSON.parse on the file contents)
 * @returns count of tiles whose gid is in the "collides: true" set
 */
export function countCollidableTiles(tmj: unknown): number {
  if (typeof tmj !== 'object' || tmj === null) return 0;
  const t = tmj as {
    tilesets?: Array<{
      firstgid?: number;
      tiles?: Array<{ id: number; properties?: Array<{ name: string; value: unknown }> }>;
    }>;
    layers?: Array<{ type?: string; data?: number[] }>;
  };

  // Collect every gid flagged collides:true across all tilesets.
  const collidesGids = new Set<number>();
  for (const ts of t.tilesets ?? []) {
    const firstgid = ts.firstgid ?? 1;
    for (const tile of ts.tiles ?? []) {
      const collides = (tile.properties ?? []).some(
        (p) => p.name === 'collides' && p.value === true,
      );
      if (collides) collidesGids.add(tile.id + firstgid);
    }
  }
  if (collidesGids.size === 0) return 0;

  // Scan every tilelayer's data array for gid matches.
  let count = 0;
  for (const layer of t.layers ?? []) {
    if (layer.type !== 'tilelayer' || !Array.isArray(layer.data)) continue;
    for (const gid of layer.data) {
      if (collidesGids.has(gid)) count++;
    }
  }
  return count;
}

/**
 * Phase 08.1 Item 2: rasterize `surfaces` polygons into a flat per-tile speed grid.
 *
 * Algorithm:
 *   1. Allocate a Float32Array of widthInTiles × heightInTiles, filled with the
 *      default speed (1.0 = scrub) for tiles not covered by any surface polygon.
 *   2. Process surfaces IN ORDER (later surfaces override earlier). For each polygon,
 *      compute its world-pixel bounding box, convert to tile range, and for every
 *      tile in that range check if the tile's CENTRE is inside the polygon via the
 *      ray-casting algorithm. If inside, write that surface's speed multiplier.
 *
 * Performance: O(S × T_bbox) where S = surface count (~98 on mouchao) and T_bbox is
 * the average tile count in a polygon's bounding box. The vast scrub background
 * polygon (~160×144 tiles) is the worst case but the ray-cast is O(V) per tile (V ≈
 * vertices); with caching of the bbox check this stays well under 1 ms at build time
 * (called once at load, not per frame).
 *
 * Exported for test coverage.
 */
export function buildSpeedGrid(
  surfaces: SurfacePolygon[],
  widthInTiles: number,
  heightInTiles: number,
  tileWidth: number,
  tileHeight: number,
): Float32Array {
  const grid = new Float32Array(widthInTiles * heightInTiles).fill(SURFACE_SPEED_DEFAULT);

  for (const surface of surfaces) {
    const speed = SURFACE_SPEEDS[surface.surfaceType] ?? SURFACE_SPEED_DEFAULT;
    const verts = surface.vertices;
    if (verts.length < 3) continue;

    // World-pixel bounding box of this polygon → tile range.
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const v of verts) {
      if (v.x < minX) minX = v.x;
      if (v.y < minY) minY = v.y;
      if (v.x > maxX) maxX = v.x;
      if (v.y > maxY) maxY = v.y;
    }
    const txMin = Math.max(0, Math.floor(minX / tileWidth));
    const tyMin = Math.max(0, Math.floor(minY / tileHeight));
    const txMax = Math.min(widthInTiles - 1, Math.floor(maxX / tileWidth));
    const tyMax = Math.min(heightInTiles - 1, Math.floor(maxY / tileHeight));

    // Ray-cast point-in-polygon for each tile centre in the bounding box.
    for (let ty = tyMin; ty <= tyMax; ty++) {
      const cy = (ty + 0.5) * tileHeight; // tile centre Y
      for (let tx = txMin; tx <= txMax; tx++) {
        const cx = (tx + 0.5) * tileWidth; // tile centre X
        if (pointInPolygon(cx, cy, verts)) {
          grid[ty * widthInTiles + tx] = speed;
        }
      }
    }
  }

  return grid;
}

/**
 * Ray-casting point-in-polygon test (Jordan curve theorem).
 * Returns true when point (px, py) is inside the closed polygon defined by `verts`.
 * Points exactly on an edge are implementation-defined (may be inside or outside) —
 * for the speed-grid use case this is inconsequential (sub-pixel edge).
 */
function pointInPolygon(px: number, py: number, verts: ReadonlyArray<{ x: number; y: number }>): boolean {
  let inside = false;
  const n = verts.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = verts[i]!.x;
    const yi = verts[i]!.y;
    const xj = verts[j]!.x;
    const yj = verts[j]!.y;
    const intersect =
      yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/**
 * Phase 08.1: extract authored surface polygons from the Tiled `surfaces` object layer.
 *
 * Each Tiled object carries a `type` property (the Class field in Tiled 1.10+) that maps
 * to a key in surface-types.json. The colour is resolved inline from SURFACE_COLORS.
 * Objects with fewer than 3 vertices (malformed or point objects) are skipped.
 * Null-safe: missing layer → empty array.
 */
export function extractSurfaces(map: Phaser.Tilemaps.Tilemap): SurfacePolygon[] {
  const out: SurfacePolygon[] = [];
  const layer = map.getObjectLayer('surfaces');
  if (!layer) return out;
  for (const obj of layer.objects) {
    const vertices = verticesFromTiledObject(obj);
    if (vertices.length < 3) continue;
    // Tiled 1.10 stores Class as `type` on the TiledObject. Fall back to '' (scrub default).
    const surfaceType = (obj.type ?? '') as string;
    const color = SURFACE_COLORS[surfaceType] ?? SURFACE_COLOR_DEFAULT;
    out.push({ vertices, color, surfaceType });
  }
  return out;
}

/**
 * Phase 08.1: extract authored roof polygons from the Tiled `roofs` object layer.
 *
 * Roofs have no surface-type property — they all render as terracotta (#b5633a)
 * which is applied at the draw site (GameScene). Only the geometry is carried here.
 * Objects with fewer than 3 vertices are skipped. Null-safe: missing layer → [].
 */
export function extractRoofs(map: Phaser.Tilemaps.Tilemap): RoofPolygon[] {
  const out: RoofPolygon[] = [];
  const layer = map.getObjectLayer('roofs');
  if (!layer) return out;
  for (const obj of layer.objects) {
    const vertices = verticesFromTiledObject(obj);
    if (vertices.length < 3) continue;
    out.push({ vertices });
  }
  return out;
}

/**
 * Phase 08.1: extract authored tree objects from the Tiled `trees` object layer.
 *
 * Trees are authored as ellipses in Tiled (ellipse: true). The bounding box
 * top-left is (obj.x, obj.y) with dimensions obj.width × obj.height.
 * We store the world-pixel centre + semi-axes for the renderer.
 *
 * Non-ellipse objects on this layer (e.g. accidentally-placed points) are skipped.
 * Null-safe: missing layer → [].
 */
export function extractTrees(map: Phaser.Tilemaps.Tilemap): TreeObject[] {
  const out: TreeObject[] = [];
  const layer = map.getObjectLayer('trees');
  if (!layer) return out;
  for (const obj of layer.objects) {
    // Only process ellipses; skip any non-ellipse objects.
    if (!obj.ellipse) continue;
    const x = typeof obj.x === 'number' && Number.isFinite(obj.x) ? obj.x : 0;
    const y = typeof obj.y === 'number' && Number.isFinite(obj.y) ? obj.y : 0;
    const w = typeof obj.width === 'number' && Number.isFinite(obj.width) && obj.width > 0 ? obj.width : 0;
    const h = typeof obj.height === 'number' && Number.isFinite(obj.height) && obj.height > 0 ? obj.height : 0;
    if (w <= 0 || h <= 0) continue;
    const rx = w / 2;
    const ry = h / 2;
    // Phase 08.1 Item 2: trunk ellipse sized at 22% of canopy (min 3 px).
    // This formula is the SINGLE SOURCE OF TRUTH shared with collision (movement.ts)
    // and rendering (GameScene.buildTreeTrunks). Do NOT recompute elsewhere.
    const trunkRx = Math.max(3, rx * 0.22);
    const trunkRy = Math.max(3, ry * 0.22);
    out.push({ cx: x + w / 2, cy: y + h / 2, rx, ry, trunkRx, trunkRy });
  }
  return out;
}

/**
 * Phase 08.1 Item 1: extract wall polygons from the Tiled `walls` object layer.
 *
 * Building walls are authored as closed polygon objects at the real Mouchao
 * orientation (~24°). They replace the over-blocking rasterised tile grid for
 * movement resolution (circle-vs-polygon instead of circle-vs-tile AABB).
 * The tile collision layer remains for A* pathfinding (coarse is fine there).
 *
 * Objects with fewer than 3 vertices are skipped. Null-safe: missing layer → [].
 */
export function extractWalls(map: Phaser.Tilemaps.Tilemap): WallPolygon[] {
  const out: WallPolygon[] = [];
  const layer = map.getObjectLayer('walls');
  if (!layer) return out;
  for (const obj of layer.objects) {
    const vertices = verticesFromTiledObject(obj);
    if (vertices.length < 3) continue;
    out.push({ vertices });
  }
  return out;
}

/**
 * Phase 08.1 Item 3: extract the out-of-bounds boundary polygon from the Tiled
 * `boundary` object layer.
 *
 * The boundary is a single closed polygon tracing the playable-area extent
 * (authored over the satellite at the real Mouchao orientation). The INSIDE is
 * walkable; everything OUTSIDE is blocked. Used for shape-precise containment in
 * movement.ts (circle-vs-boundary) alongside the converter's tile-grid raster
 * (for A* and FOV). The first object with ≥3 vertices wins.
 *
 * Null-safe: missing layer or no valid polygon → empty array (map is unbounded;
 * movement/converter treat <3 vertices as "no boundary").
 */
export function extractBoundary(map: Phaser.Tilemaps.Tilemap): Vec2[] {
  const layer = map.getObjectLayer('boundary');
  if (!layer) return [];
  for (const obj of layer.objects) {
    const vertices = verticesFromTiledObject(obj);
    if (vertices.length >= 3) return vertices;
  }
  return [];
}

/**
 * Phase 08.1 Item 2: extract closed obstacle polygons from the Tiled `obstacles` layer.
 *
 * Obstacles are closed polygon objects (bench, urn, low wall, planter). They block
 * movement but NOT sight — the player can see over them but cannot walk through.
 *
 * Objects with fewer than 3 vertices are skipped. Null-safe: missing layer → [].
 */
export function extractObstacles(map: Phaser.Tilemaps.Tilemap): ObstaclePolygon[] {
  const out: ObstaclePolygon[] = [];
  const layer = map.getObjectLayer('obstacles');
  if (!layer) return out;
  for (const obj of layer.objects) {
    const vertices = verticesFromTiledObject(obj);
    if (vertices.length < 3) continue;
    out.push({ vertices });
  }
  return out;
}

/**
 * Phase 08.1 Item 2: extract fence polylines from the Tiled `fences` layer.
 *
 * Fences are open polyline objects (stone wall segments, wire fences, hedges). They
 * block movement but NOT sight. Phaser's TiledObject does not formally declare
 * `polyline` in its .d.ts but the field is present at runtime for polyline objects.
 *
 * Objects with fewer than 2 points are skipped. Null-safe: missing layer → [].
 */
export function extractFences(map: Phaser.Tilemaps.Tilemap): FencePolyline[] {
  const out: FencePolyline[] = [];
  const layer = map.getObjectLayer('fences');
  if (!layer) return out;
  for (const obj of layer.objects) {
    const ox = obj.x ?? 0;
    const oy = obj.y ?? 0;
    // `polyline` is not in Phaser's TiledObject typedef but is present at runtime.
    const polyline = (obj as unknown as { polyline?: Array<{ x: number; y: number }> }).polyline;
    if (!Array.isArray(polyline) || polyline.length < 2) continue;
    const points: Vec2[] = polyline.map((p) => ({ x: ox + p.x, y: oy + p.y }));
    out.push({ points });
  }
  return out;
}

/**
 * Phase 08.1 Item 2: build the movement-only walkability grid.
 *
 * Starts from a deep copy of `baseGrid` (the sight grid: walls + tree-trunks)
 * and rasterizes obstacle polygons and fence polylines into it, marking the
 * covered tiles as blocked (1). The base `grid` (sight) is NEVER mutated.
 *
 * Algorithm:
 *   Obstacles — point-in-polygon ray-cast per tile centre in the polygon bbox.
 *   Fences    — point-to-segment distance per tile centre; tiles within 0.5 × tileWidth
 *               (½ tile ≈ 16 px for 32 px tiles) of any fence segment are blocked.
 *               This gives the fence a collision width of ≈ 1 tile total, matching
 *               the visual thickness authored in Tiled (a thin drawn line becomes
 *               a one-tile-wide movement barrier so it's actually impenetrable).
 *
 * Performance: called once at scene load; O(O × T_bbox + F × segments × T_seg_bbox)
 * where O = obstacle count (~4) and F = fence count (~4). Negligible at load time.
 *
 * Exported for unit-test coverage.
 */
export function buildMovementGrid(
  baseGrid: WalkabilityGrid,
  obstacles: ObstaclePolygon[],
  fences: FencePolyline[],
  widthInTiles: number,
  heightInTiles: number,
  tileWidth: number,
  tileHeight: number,
): WalkabilityGrid {
  // Deep copy so sight grid is never mutated.
  const grid: WalkabilityGrid = baseGrid.map((row) => [...row]);

  // ── Rasterize obstacle polygons ───────────────────────────────────────────
  for (const obstacle of obstacles) {
    const verts = obstacle.vertices;
    if (verts.length < 3) continue;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const v of verts) {
      if (v.x < minX) minX = v.x;
      if (v.y < minY) minY = v.y;
      if (v.x > maxX) maxX = v.x;
      if (v.y > maxY) maxY = v.y;
    }
    const txMin = Math.max(0, Math.floor(minX / tileWidth));
    const tyMin = Math.max(0, Math.floor(minY / tileHeight));
    const txMax = Math.min(widthInTiles - 1, Math.floor(maxX / tileWidth));
    const tyMax = Math.min(heightInTiles - 1, Math.floor(maxY / tileHeight));
    for (let ty = tyMin; ty <= tyMax; ty++) {
      for (let tx = txMin; tx <= txMax; tx++) {
        const cx = (tx + 0.5) * tileWidth;
        const cy = (ty + 0.5) * tileHeight;
        if (pointInPolygon(cx, cy, verts)) {
          grid[ty]![tx] = 1;
        }
      }
    }
  }

  // ── Rasterize fence polylines ─────────────────────────────────────────────
  // Thicken each segment by 0.5 × tileWidth on each side (total 1-tile band).
  const halfThickness = tileWidth * 0.5;
  const halfThicknessSq = halfThickness * halfThickness;
  for (const fence of fences) {
    const pts = fence.points;
    for (let i = 0; i + 1 < pts.length; i++) {
      const ax = pts[i]!.x, ay = pts[i]!.y;
      const bx = pts[i + 1]!.x, by = pts[i + 1]!.y;
      const segMinX = Math.min(ax, bx) - halfThickness;
      const segMinY = Math.min(ay, by) - halfThickness;
      const segMaxX = Math.max(ax, bx) + halfThickness;
      const segMaxY = Math.max(ay, by) + halfThickness;
      const txMin = Math.max(0, Math.floor(segMinX / tileWidth));
      const tyMin = Math.max(0, Math.floor(segMinY / tileHeight));
      const txMax = Math.min(widthInTiles - 1, Math.ceil(segMaxX / tileWidth));
      const tyMax = Math.min(heightInTiles - 1, Math.ceil(segMaxY / tileHeight));
      for (let ty = tyMin; ty <= tyMax; ty++) {
        for (let tx = txMin; tx <= txMax; tx++) {
          const cx = (tx + 0.5) * tileWidth;
          const cy = (ty + 0.5) * tileHeight;
          if (pointToSegmentDistSq(cx, cy, ax, ay, bx, by) <= halfThicknessSq) {
            grid[ty]![tx] = 1;
          }
        }
      }
    }
  }

  return grid;
}

/**
 * Squared distance from point (px, py) to the closest point on segment (ax,ay)→(bx,by).
 * Used by buildMovementGrid for fence thickening. Returns squared distance to avoid
 * unnecessary sqrt (we compare against halfThickness²).
 */
function pointToSegmentDistSq(
  px: number, py: number,
  ax: number, ay: number,
  bx: number, by: number,
): number {
  const dx = bx - ax;
  const dy = by - ay;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return (px - ax) ** 2 + (py - ay) ** 2;
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lenSq));
  const projX = ax + t * dx;
  const projY = ay + t * dy;
  return (px - projX) ** 2 + (py - projY) ** 2;
}

/** Normalises rectangle or polygon Tiled objects to an absolute-pixel vertex list. */
function verticesFromTiledObject(obj: Phaser.Types.Tilemaps.TiledObject): Vec2[] {
  const ox = obj.x ?? 0;
  const oy = obj.y ?? 0;
  if (obj.polygon && Array.isArray(obj.polygon) && obj.polygon.length >= 3) {
    return obj.polygon.map((p) => ({ x: ox + p.x, y: oy + p.y }));
  }
  if (typeof obj.width === 'number' && typeof obj.height === 'number' && obj.width > 0 && obj.height > 0) {
    // Rectangle → 4-vertex polygon (clockwise).
    return [
      { x: ox,             y: oy },
      { x: ox + obj.width, y: oy },
      { x: ox + obj.width, y: oy + obj.height },
      { x: ox,             y: oy + obj.height },
    ];
  }
  return [];
}
